import { useQuery } from '@tanstack/react-query'

import { filePresenceQueryKeys } from '@/hooks/files/types'

import type { UUID } from '@/types/Id'

import { useFilePresenceLoader } from '@/services/file-presence-loader/context'

interface UseFileEditorsArgs {
  fileId: UUID | ''
  enabled?: boolean
}

/**
 * Who has this file open in the editor, or null where nobody has — including a
 * file never opened in the editor at all. Ask once per file and let the loader
 * gather them: a screen of files is one request however many are on it.
 *
 * Asking again is the loader provider's job, on one timer for the whole screen,
 * so that every file on it asks in the same tick and stays one request.
 */
export const useFileEditors = ({
  fileId,
  enabled = true,
}: UseFileEditorsArgs) => {
  const loader = useFilePresenceLoader()

  return useQuery({
    queryKey: filePresenceQueryKeys.file({ fileId }),
    queryFn: () => (fileId === '' ? null : loader!.load(fileId)),
    enabled: enabled && fileId !== '' && !!loader,
    // The next poll is seconds away, and nobody waits on this the way they wait on a document.
    retry: false,
  })
}
